import { AppRoutes } from 'config/routes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { useTranslation } from 'react-i18next';
import {
  useCreateSupplyProductionDocumentMutation,
  useGetControlCardSchemeQuery,
  useGetCoreDocumentsNoCacheMutation,
  useSignDocumentMutation,
  useUpdateControlCardMutation,
} from 'store/api/documents';
import { useHandleMutation } from 'utils/handleMutation';
import {
  generateModelFromScheme,
  generateValidationSchema,
  parseSteps,
} from 'utils/sheme-parser';

export const useNewTask = () => {
  const { id: documentId } = useParams();
  const navigate = useNavigate();

  const {
    i18n: { language },
  } = useTranslation();

  const path = useLocation().pathname;
  const { Home, Contracts } = AppRoutes.Dashboard;

  const [step, setStep] = useState<number>(1);

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [contractId, setContractId] = useState<string | undefined>();

  const [fetchDocument] = useGetCoreDocumentsNoCacheMutation({});

  const [signDocument, signDocumentMutation] = useSignDocumentMutation();

  const { data: controlCardkDocumentScheme } = useGetControlCardSchemeQuery(
    {
      documentId: documentId ?? '',
    },
    { skip: !documentId },
  );

  useCreateSupplyProductionDocumentMutation();
  const [updateItem, updateItemMutation] = useUpdateControlCardMutation();

  const dropDownOptions = useMemo(
    () =>
      [
        controlCardkDocumentScheme && {
          value: 'ControlCard',
          label: 'Контрольный талон',
          scheme: controlCardkDocumentScheme,
        },
      ]?.filter((item) => item),
    [controlCardkDocumentScheme],
  );

  const [currentSelect, setCurrentSelect] = useState(dropDownOptions[0]);
  const isApproval = path.includes(AppRoutes.Dashboard.Contracts.ContractApproval);

  const handleSign = async () => {
    const { data } = (await fetchDocument({ ids: [contractId ?? documentId ?? ''] })) as any;

    const [document] = data ?? [];

    await signDocument({
      signature: null,
      detachedSignature: null,
      documentId: document?.id ?? '',
      documentVersion: document?.version ?? 0,
    });
  };

  useEffect(() => {
    const handle = async () => {
      if (contractId?.length) {
        handleSign();
      }
    };

    handle();
  }, [contractId]);

  const onSuccessCreate = useCallback(async (id: string) => {
    setContractId(id);
  }, []);

  const handleClose = useCallback(() => {
    navigate(Home + AppRoutes.Dashboard.ControlCards.Home);
  }, [navigate]);

  const onSuccessSign = useCallback(async () => {
    setIsProcessing(true);
    await new Promise((resolve) => setTimeout(resolve, 5000));
    setIsProcessing(false);
    handleClose();
  }, [handleClose]);

  const onErrorSign = useCallback((err: any) => {
    console.error('Error sign', err);
  }, []);

  useHandleMutation({
    ...updateItemMutation,
    onSuccess: onSuccessCreate,
  });

  useHandleMutation({
    ...signDocumentMutation,
    onSuccess: onSuccessSign,
    onError: onErrorSign,
  });

  useEffect(() => {
    setCurrentSelect(dropDownOptions[0]);
  }, [dropDownOptions, controlCardkDocumentScheme]);

  const { currentScheme, model, steps, stepsLabels } = useMemo(() => {
    return {
      currentScheme: currentSelect?.scheme,
      model: generateModelFromScheme(currentSelect?.scheme, language),
      ...parseSteps(currentSelect?.scheme),
    };
  }, [currentSelect]);

  const getValidationSchema = () => {
    if (isApproval) {
      return Yup.object().shape({});
    }
    return generateValidationSchema(currentScheme, step);
  };

  return {
    step,
    steps,
    model,
    setStep,
    isProcessing,
    handleSign,
    isApproval,
    updateItem,
    stepsLabels,
    handleClose,
    currentSelect,
    currentScheme,
    dropDownOptions,
    setCurrentSelect,
    getValidationSchema,
    isLoading: signDocumentMutation.isLoading || updateItemMutation.isLoading,
  };
};
